import type {
  OrgPlanningScenarioReview,
  OrgPlanningScenarioReviewDiff,
} from '../models/OrgPlanningScenarioReview';

export interface OperationalChangeDiffItem {
  readonly key: string;
  readonly title: string;
  readonly detail: string | null;
  readonly tone: 'add' | 'change' | 'remove';
  readonly fieldChanges?: readonly OperationalChangeField[];
}

export interface OperationalChangeField {
  readonly field: string;
  readonly value: unknown;
}

export interface OperationalImpactChange {
  readonly key: string;
  readonly label: string;
  readonly before: number;
  readonly after: number;
}

const IMPACT_LABELS: Readonly<Record<string, string>> = {
  positionCount: 'Positions',
  primaryIncumbentCount: 'Filled seats',
  vacancyCount: 'Open seats',
  successorCount: 'Successor nominations',
  membershipCount: 'Memberships',
  commitmentCount: 'Commitments',
  demandCount: 'Workforce demands',
  totalDemandFte: 'Demand FTE',
  totalCommittedFte: 'Committed FTE',
  knownGapFte: 'Known gap FTE',
};

export function presentOperationalDiff(
  diff: readonly OrgPlanningScenarioReviewDiff[],
): OperationalChangeDiffItem[] {
  const consumed = new Set<number>();
  const items: OperationalChangeDiffItem[] = [];

  diff.forEach((entry, index) => {
    if (consumed.has(index)) return;
    const hirePair = newPersonAssignmentPair(diff, index, consumed);
    if (hirePair) {
      const [personIndex, assignmentIndex] = hirePair;
      const person = diff[personIndex];
      const assignment = diff[assignmentIndex];
      consumed.add(personIndex);
      consumed.add(assignmentIndex);
      items.push({ key: `fill-role:${person.externalId}:${assignment.externalId}`,
        title: `Add ${person.display?.entityLabel ?? 'person'} and assign to ${assignment.display?.objectLabel ?? 'role'}`,
        detail: [presentEntity(person).detail, assignment.after ? effectiveDetail(assignment.after) : null].filter(Boolean).join(' ') || null,
        tone: 'add' });
      return;
    }
    const pairedIndex = reportingMovePairIndex(diff, index, consumed);
    if (pairedIndex !== null) {
      const paired = diff[pairedIndex];
      const addition = isReportingAddition(entry) ? entry : paired;
      const retirement = addition === entry ? paired : entry;
      consumed.add(index);
      consumed.add(pairedIndex);
      items.push(presentReportingMove(addition, retirement));
      return;
    }
    consumed.add(index);
    items.push({
      key: `${entry.factKind}:${entry.externalId}:${entry.operation}`,
      ...presentEntry(entry),
    });
  });

  return items;
}

/** Group only one newly created identity and its sole new assignment, never labels. */
function newPersonAssignmentPair(diff: readonly OrgPlanningScenarioReviewDiff[], index: number, consumed: ReadonlySet<number>): [number, number] | null {
  const entry = diff[index];
  const personIndex = entry.factKind === 'person' ? index : entry.predicate === 'occupies'
    ? diff.findIndex((person) => person.factKind === 'person'
      && person.externalId.replace(/^record:/, '') === relationshipRef(entry, 'subjectRef')?.replace(/^record:/, '')) : -1;
  if (personIndex < 0 || consumed.has(personIndex)) return null;
  const person = diff[personIndex];
  if (person.before || !person.after || person.operation !== 'upsert') return null;
  const assignments = diff.flatMap((assignment, candidateIndex) => assignment.predicate === 'occupies'
    && !assignment.before && assignment.after && assignment.operation === 'upsert' && !consumed.has(candidateIndex)
    && relationshipRef(assignment, 'subjectRef')?.replace(/^record:/, '') === person.externalId.replace(/^record:/, '')
    ? [candidateIndex] : []);
  return assignments.length === 1 && (index === personIndex || index === assignments[0]) ? [personIndex, assignments[0]] : null;
}

export function presentOperationalImpact(
  impact: OrgPlanningScenarioReview['impact'],
): OperationalImpactChange[] {
  return Object.entries(IMPACT_LABELS).flatMap(([key, label]) => {
    const before = impact.before[key];
    const after = impact.after[key];
    if (typeof before !== 'number' || typeof after !== 'number' || before === after) return [];
    return [{ key, label, before, after }];
  });
}

function presentEntry(entry: OrgPlanningScenarioReviewDiff): Omit<OperationalChangeDiffItem, 'key'> {
  if (entry.factKind !== 'relationship') return presentEntity(entry);
  const subject = entry.display?.subjectLabel ?? 'Person or position';
  const object = entry.display?.objectLabel ?? 'target';
  const before = entry.before;
  const after = entry.after;

  if (entry.predicate === 'occupies') {
    if (!after) return item(`Open ${object}`, `${subject} will no longer occupy this seat.`, 'remove');
    if (!before) return item(`Assign ${subject} to ${object}`, effectiveDetail(after), 'add');
    if (dateChanged(before, after, 'validTo')) {
      return item(`End ${subject}’s assignment to ${object}`, effectiveDetail(after, 'validTo'), 'remove');
    }
    return item(`Update ${subject}’s assignment to ${object}`, qualifierChanges(before, after), 'change');
  }

  if (entry.predicate === 'reports_to' || entry.predicate === 'matrix_reports_to') {
    const kind = entry.predicate === 'matrix_reports_to' ? 'matrix reporting line' : 'reporting line';
    if (!after) return item(`Remove ${subject}’s ${kind}`, `Previously reported to ${object}.`, 'remove');
    if (!before) return item(`Set ${subject} to report to ${object}`, effectiveDetail(after), 'add');
    if (dateChanged(before, after, 'validTo')) {
      return item(`End ${subject}’s ${kind}`, `Previous manager: ${object}. ${effectiveDetail(after, 'validTo') ?? ''}`.trim(), 'remove');
    }
    return item(`Change ${subject}’s ${kind}`, `New manager: ${object}.`, 'change');
  }

  if (entry.predicate === 'successor_of') {
    if (!after) return item(`Retire ${subject}’s nomination for ${object}`, 'The nomination remains in history.', 'remove');
    if (!before) return item(`Nominate ${subject} for ${object}`, qualifierChanges(before, after), 'add');
    return item(`Update ${subject}’s nomination for ${object}`, qualifierChanges(before, after), 'change');
  }

  const relation = readable(entry.predicate ?? 'relationship');
  if (!after) return item(`Remove ${relation}`, `${subject} → ${object}`, 'remove');
  if (!before) return item(`Add ${relation}`, `${subject} → ${object}`, 'add');
  return item(`Update ${relation}`, `${subject} → ${object}`, 'change');
}

function reportingMovePairIndex(
  diff: readonly OrgPlanningScenarioReviewDiff[],
  index: number,
  consumed: ReadonlySet<number>,
): number | null {
  const entry = diff[index];
  if (!isReportingAddition(entry) && !isReportingRetirement(entry)) return null;
  const subjectRef = relationshipRef(entry, 'subjectRef');
  if (!subjectRef) return null;

  const match = diff.findIndex((candidate, candidateIndex) => {
    if (candidateIndex === index || consumed.has(candidateIndex)) return false;
    if (entry.predicate !== candidate.predicate) return false;
    if (relationshipRef(candidate, 'subjectRef') !== subjectRef) return false;
    if (isReportingAddition(entry) === isReportingAddition(candidate)) return false;
    const addition = isReportingAddition(entry) ? entry : candidate;
    const retirement = addition === entry ? candidate : entry;
    const newManagerRef = relationshipRef(addition, 'objectRef');
    const oldManagerRef = relationshipRef(retirement, 'objectRef');
    if (!newManagerRef || !oldManagerRef || newManagerRef === oldManagerRef) return false;
    const startsAt = stringValue(addition.after?.validFrom);
    const endsAt = stringValue(retirement.after?.validTo);
    return Boolean(startsAt && endsAt && startsAt === endsAt);
  });

  return match >= 0 ? match : null;
}

function presentReportingMove(
  addition: OrgPlanningScenarioReviewDiff,
  retirement: OrgPlanningScenarioReviewDiff,
): OperationalChangeDiffItem {
  const subject = addition.display?.subjectLabel ?? retirement.display?.subjectLabel ?? 'Person or position';
  const nextManager = addition.display?.objectLabel ?? 'new manager';
  const previousManager = retirement.display?.objectLabel ?? 'previous manager';
  const effective = addition.after ? effectiveDetail(addition.after) : null;
  return {
    key: `reporting-move:${retirement.externalId}:${addition.externalId}`,
    title: `Change ${subject}’s reporting line`,
    detail: `Previous manager: ${previousManager}. New manager: ${nextManager}.${effective ? ` ${effective}` : ''}`,
    tone: 'change',
  };
}

function isReportingAddition(entry: OrgPlanningScenarioReviewDiff): boolean {
  return isReporting(entry) && entry.before === null && entry.after !== null;
}

function isReportingRetirement(entry: OrgPlanningScenarioReviewDiff): boolean {
  if (!isReporting(entry) || entry.before === null) return false;
  if (entry.after === null) return true;
  return dateChanged(entry.before, entry.after, 'validTo');
}

function isReporting(entry: OrgPlanningScenarioReviewDiff): boolean {
  return entry.factKind === 'relationship'
    && (entry.predicate === 'reports_to' || entry.predicate === 'matrix_reports_to');
}

function relationshipRef(
  entry: OrgPlanningScenarioReviewDiff,
  key: 'subjectRef' | 'objectRef',
): string | null {
  return stringValue(entry.after?.[key]) ?? stringValue(entry.before?.[key]);
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function presentEntity(entry: OrgPlanningScenarioReviewDiff): Omit<OperationalChangeDiffItem, 'key'> {
  const kind = readable(entry.factKind);
  const label = entry.display?.entityLabel ?? `Unnamed ${kind}`;
  if (!entry.after) return item(`Retire ${label}`, `This ${kind} remains available in history.`, 'remove');
  const fields = changedFields(entry.before, entry.after);
  if (!entry.before) return { ...item(`Create ${label}`, fields.detail, 'add'), fieldChanges: fields.fields };
  return { ...item(`Update ${label}`, fields.detail, 'change'), fieldChanges: fields.fields };
}

function item(
  title: string,
  detail: string | null,
  tone: OperationalChangeDiffItem['tone'],
): Omit<OperationalChangeDiffItem, 'key'> {
  return { title, detail, tone };
}

function changedFields(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>>,
): { detail: string | null; fields: readonly OperationalChangeField[] } {
  const ignored = new Set(['kind', 'predicate', 'subjectRef', 'objectRef', 'qualifiers']);
  const fields = Object.keys(after)
    .filter((key) => !ignored.has(key) && !same(before?.[key], after[key]))
    .slice(0, 4)
    .map((field) => ({ field, value: after[field] }));
  return {
    detail: fields.length > 0 ? fields.map(({ field, value }) => `${readable(field)}: ${displayValue(value)}`).join(' · ') : null,
    fields,
  };
}

function qualifierChanges(
  before: Readonly<Record<string, unknown>> | null,
  after: Readonly<Record<string, unknown>>,
): string | null {
  const beforeQualifiers = record(before?.qualifiers);
  const afterQualifiers = record(after.qualifiers);
  return changedFields(beforeQualifiers, afterQualifiers).detail ?? effectiveDetail(after);
}

function effectiveDetail(value: Readonly<Record<string, unknown>>, dateKey = 'validFrom'): string | null {
  const date = value[dateKey];
  if (typeof date !== 'string' || !date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${dateKey === 'validTo' ? 'Ends' : 'Effective'} ${new Intl.DateTimeFormat('en', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(parsed)}.`;
}

function dateChanged(
  before: Readonly<Record<string, unknown>>,
  after: Readonly<Record<string, unknown>>,
  key: string,
): boolean {
  return !same(before[key], after[key]) && typeof after[key] === 'string';
}

function record(value: unknown): Readonly<Record<string, unknown>> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Readonly<Record<string, unknown>>
    : {};
}

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return 'Updated';
}

function readable(value: string): string {
  return value.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}
